import { StemType } from '@arpeggi/shared'
import React from 'react'
import { useDrag, useDrop } from 'react-dnd'
import { useTreeState } from '../../../stores/Root'
import { StudioDraggableTypes } from '../StudioUtils'
import useTrackDrop from './useTrackDrop'

const useInstrumentTrackHeader = (stemIndex: number) => {
  const ref = React.useRef(null)
  const [forbidDrag, setForbidDrag] = React.useState(false)
  const [isHover, setIsHover] = React.useState(false)
  const { studio } = useTreeState()
  const isSelected = stemIndex === studio.selectedStemIndex
  const isSolo = stemIndex === studio.soloTrackIndex
  const stem = studio.song.stems[stemIndex]

  // for reordering instrument tracks
  const { isTrackOver, connectTrackDrop } = useTrackDrop(stemIndex)

  // for changing the sample or stem of an existing track
  const [{ isOver }, connectDrop] = useDrop(
    () => ({
      accept: [
        stem.type() === StemType.INSTRUMENT ? StudioDraggableTypes.STEM : '',
        !stem.isRemixed ? StudioDraggableTypes.SAMPLE : ''
      ],
      drop: (item: any) => {
        // Item is a remixableStemMetadata object.
        if (item.type === StudioDraggableTypes.STEM) {
          studio.updateStemWithProtoRemix(stemIndex, item.remixableStemMetadata)
        } else if (item.type === StudioDraggableTypes.SAMPLE) {
          switch (stem.type()) {
            case StemType.DRUMS:
              studio.addDrumSampleToStem(item.sample, stemIndex)
              break
            case StemType.INSTRUMENT:
              studio.updateMelodicStemSample(stemIndex, item.sample)
              break
            case StemType.AUDIO:
              studio.addClipFromSampleToAudioStem(item.sample, stemIndex)
              break
          }
        }
      },
      collect: (monitor) => ({
        isOver: !!monitor.isOver()
      })
    }),
    [stemIndex]
  )

  // for reordering tracks
  const [{ isDragging }, connectDrag] = useDrag(
    () => ({
      type: StudioDraggableTypes.TRACK,
      item: { type: StudioDraggableTypes.TRACK, stemIndex },
      canDrag: !forbidDrag,
      collect: (monitor) => ({
        isDragging: !!monitor.isDragging()
      })
    }),
    [forbidDrag, stemIndex]
  )

  const deleteStem = () => {
    // If user removes the currently selected stem, select -1.
    if (isSelected) {
      studio.unselectStem()
    } else if (studio.selectedStemIndex !== -1 && studio.selectedStemIndex > stemIndex) {
      studio.setSelectedStemIndex(studio.selectedStemIndex - 1)
    }
    studio.removeStem(stemIndex)
  }

  connectTrackDrop(ref)
  connectDrag(ref)
  connectDrop(ref)

  return {
    isTrackOver,
    isSelected,
    isOver,
    isDragging,
    setIsHover,
    ref,
    setForbidDrag,
    isHover,
    deleteStem,
    isSolo
  }
}

export default useInstrumentTrackHeader
